import React, { useEffect, useState } from 'react';
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';
import { CriticalError } from '@revenuewell/page-critical-error';
import { IAppTemplateProps } from './types/app-template-props';
import { PrivateAppTemplateProviders } from '../app-template-providers/private-app-template-providers';
import { PublicAppTemplateProviders } from '../app-template-providers/public-app-template-providers';
import {
  EventBus,
  EventFrameActivate,
  EventNavigation,
  ProxyProvider,
  useProxy
} from '@revenuewell/logic-wrapper-proxy';
import { AppSpinner, ProgressProvider, useProgress } from '@revenuewell/logic-progress';
import { TraceWindowNavigation } from './navigation-tracer';
import { SignInCallback, SignInRedirectPaths, SignInRedirectProviders } from '@revenuewell/logic-oidc-signin-callback';

export function AppTemplate(props: React.PropsWithChildren<IAppTemplateProps>) {
  return (
    <ProxyProvider>
      <ProgressProvider>
        <AppTemplateBase {...props} />
      </ProgressProvider>
    </ProxyProvider>
  );
}

export function AppTemplateBase(props: React.PropsWithChildren<IAppTemplateProps>) {
  const prodConfigPath = props.prodConfigPath ?? 'config/config.json';
  const privateRoutes = props.routes.filter((r) => {
    return r.private;
  });
  const publicRoutes = props.routes.filter((r) => {
    return !r.private;
  });
  const { showAppSpinner } = useProgress();
  const { proxy, proxyPath, proxyFrame, sendEvent, listenEvent } = useProxy();
  const [isActivated, setIsActivated] = useState(false);

  useEffect(() => {
    if (proxy) {
      forceFrameActivation()
        ? activateFrame()
        : listenEvent('frame-activate', (event: EventBus) => {
            const { frameName } = (event as EventFrameActivate).data;
            if (frameName.toLowerCase() === proxyFrame.toLowerCase()) activateFrame();
          });
      return syncNavigationWithWrapper();
    } else {
      showAppSpinner();
      setIsActivated(true);
    }
  }, []);

  const syncNavigationWithWrapper = () => {
    TraceWindowNavigation();
    window.addEventListener('navigate', handleNavigation);
    return () => window.removeEventListener('navigate', handleNavigation);
  };

  const handleNavigation = (event: any) => {
    sendNavigationEvent(event.detail.url, event.detail.replaceState);
  };

  const forceFrameActivation = () => {
    const forceFrameActivationKey = 'forceFrameActivation';
    const query = new URLSearchParams(window.location.search);
    const value = query.get(forceFrameActivationKey);
    return value !== null && value.toLocaleLowerCase() !== 'false';
  };

  const activateFrame = () => {
    const location = window.location;
    setIsActivated(true);
    sendNavigationEvent(location.pathname + location.search + location.hash, true);
  };

  const sendNavigationEvent = (path: string, replaceState = false) => {
    const url = new URL(window.location.origin + path);

    sendEvent({
      messageType: 'navigate',
      data: {
        pathname: url.pathname,
        search: url.search,
        hash: url.hash,
        replaceState: replaceState
      }
    } as EventNavigation);
  };

  return (
    <>
      <BrowserRouter basename={proxy ? proxyPath : undefined}>
        <Switch>
          <Route exact path={SignInRedirectPaths}>
            <SignInRedirectProviders
              isProduction={props.isProduction}
              devConfig={props.devConfig}
              prodConfigPath={prodConfigPath}
            >
              <SignInCallback />
            </SignInRedirectProviders>
          </Route>

          {isActivated && (
            <>
              <Route exact path={privateRoutes.map((r) => r.path!)}>
                <PrivateAppTemplateProviders {...props}>{props.children}</PrivateAppTemplateProviders>
              </Route>

              <Route exact path={publicRoutes.map((r) => r.path!)}>
                <PublicAppTemplateProviders {...props}>{props.children}</PublicAppTemplateProviders>
              </Route>
            </>
          )}

          <Route exact path="/critical-error">
            <CriticalError />
          </Route>

          {props.defaultRedirect && (
            <Route exact path="/">
              <Redirect to={props.defaultRedirect} />
            </Route>
          )}
        </Switch>
      </BrowserRouter>
      <AppSpinner />
    </>
  );
}
